import { useCallback, useRef, useState } from "react";
import { apiFetch, ApiError } from "../lib/api";

export type ChallengeInfo = {
  status: string;
  device_name: string;
  device_location: string;
  center_name: string;
  expires_at: string;
};

export type ScannerPhase =
  | { step: "closed" }
  | { step: "scanning" }
  | { step: "loading"; token: string }
  | { step: "confirm"; token: string; challenge: ChallengeInfo }
  | { step: "confirming"; token: string }
  | { step: "success"; device_name: string; device_location: string }
  | { step: "error"; message: string; canRetry: boolean }
  | { step: "permission-denied" };

/**
 * Extracts a kiosk token from a scanned QR code URL.
 * Returns null if the URL doesn't match the expected format.
 */
function extractKioskToken(text: string): string | null {
  try {
    const url = new URL(text);
    if (url.hostname !== "app.cradleos.com") return null;
    if (url.pathname !== "/kiosk-auth") return null;
    return url.searchParams.get("token");
  } catch {
    return null;
  }
}

export function useQrScanner() {
  const [phase, setPhase] = useState<ScannerPhase>({ step: "closed" });
  const abortRef = useRef<AbortController | null>(null);

  const open = useCallback(() => {
    setPhase({ step: "scanning" });
  }, []);

  const close = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPhase({ step: "closed" });
  }, []);

  const onPermissionDenied = useCallback(() => {
    setPhase({ step: "permission-denied" });
  }, []);

  const onScan = useCallback(async (decodedText: string) => {
    const token = extractKioskToken(decodedText);
    if (!token) return; // silently ignore non-kiosk QR codes

    setPhase({ step: "loading", token });

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data } = await apiFetch<ChallengeInfo>(
        `/api/v1/auth/kiosk_qr_confirm?token=${encodeURIComponent(token)}`,
        { signal: controller.signal }
      );
      setPhase({ step: "confirm", token, challenge: data });
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof ApiError) {
        setPhase({ step: "error", message: err.errors[0] ?? "An unexpected error occurred.", canRetry: false });
      } else {
        setPhase({ step: "error", message: "Unable to load QR code details.", canRetry: true });
      }
    }
  }, []);

  const confirm = useCallback(async (token: string) => {
    setPhase({ step: "confirming", token });

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data } = await apiFetch<{ confirmed: boolean; device_name: string; device_location: string }>(
        "/api/v1/auth/kiosk_qr_confirm",
        { method: "POST", body: { token }, signal: controller.signal }
      );
      setPhase({
        step: "success",
        device_name: data.device_name,
        device_location: data.device_location,
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof ApiError) {
        setPhase({ step: "error", message: err.errors[0] ?? "An unexpected error occurred.", canRetry: false });
      } else {
        setPhase({ step: "error", message: "Failed to confirm. Please try again.", canRetry: true });
      }
    }
  }, []);

  const retry = useCallback(() => {
    setPhase({ step: "scanning" });
  }, []);

  return { phase, open, close, onScan, onPermissionDenied, confirm, retry };
}
